import {
    Dialog,
    Box,
    Container,
    Button,
    Badge
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {noun as plural} from "plural-ru";
import {CargoList} from "./CargoList";
import {Cargo} from "~/apps/packages/warehouse-closure/queries";
import {SearchAppBar} from "~/apps/core/ui/SearchAppBar";
import makeStyles from '@mui/styles/makeStyles';
import {PortTheme} from "~/ui/theme";
import {useHistoryState} from "~/apps/core/utils";
import {tuple} from "~/types";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {useTheme} from "@mui/styles";
import {AbsoluteContainer} from "~/apps/core/ui/AbsoluteContainer";


export type CargoSelectDialogProps<V> = {
    open?: boolean
    onClose?: () => any
    value?: V[]
    onChange?: (value: V[]) => any
}


const useStyles = makeStyles<PortTheme, CargoSelectDialogProps<any>>(theme => ({
    paper: {
        backgroundColor: theme.palette.background.default
    },
    badge: {
        "& .MuiBadge-badge": {
            position: "inherit !important",
            transform: "none",
        },
    },
}));


type FilterState = {
    search?: string
}


function useFilterState() {
    const [value, setValue, delValue] = useHistoryState<FilterState>({
        name: "f",
        serialize: value => JSON.stringify(value),
        handleBack: false,
        deserialize: data => {
            const raw = JSON.parse(data);
            const value: FilterState = {};
            if (typeof raw == "object") {
                if (typeof raw.search == "string" && raw.search.length > 0) {
                    value.search = raw.search;
                }
            }
            return Object.keys(value).length ? value : null;
        },
    });

    const setFilter = useCallback(async value => {
        if (value?.search?.length) {
            setValue(value);
        } else {
            await delValue();
        }
    }, [setValue, delValue]);

    return tuple(value, setFilter);
}


export const CargoSelectDialog = <V extends Pick<Cargo, "code" | "id">>(props: CargoSelectDialogProps<V>) => {
    const {open, onClose, value, onChange} = props;
    const theme = useTheme<PortTheme>();
    const classes = useStyles(props);
    const [filter, setFilter] = useFilterState();
    const [selectedCargos, setSelectedCargos] = useState<V[]>([]);
    // обновляем исключающие грузы только при открытии/закрытии окна
    const excludeCodes = useMemo(() => {
        return value.map(cargo => cargo.code);
    }, [open]);

    useEffect(() => {
        if (!open) {
            setTimeout(() => {
                setSelectedCargos([]);
                setFilter({}).then(() => null);
            }, theme.transitions.duration.leavingScreen);
        }
    }, [open]);

    const handleClose = () => {
        onClose && onClose();
    }

    const handleSearch = (value) => {
        setFilter({
            ...filter,
            search: value,
        }).then(() => null);
    }

    const handleItemClick = useCallback((item: Cargo) => {
        setSelectedCargos(value => {
            const index = value.findIndex(x => x.id == item.id);
            if (index != -1) {
                value.splice(index, 1);
                return [...value];
            } else {
                return [...value, item as any];
            }
        });
    }, [setSelectedCargos]);

    const handleSelectClick = useCallback(() => {
        onChange && onChange([...value, ...selectedCargos]);
    }, [onChange, selectedCargos, value]);

    return (
        <Dialog open={open} fullScreen={true} onClose={handleClose} PaperProps={{className: classes.paper}}>
            <SearchAppBar
                title={filter?.search || "Выбор груза"}
                position="fixed"
                color="default"
                query={filter?.search}
                onSearch={handleSearch}
            />

            <AbsoluteContainer disableGutters top={56}>
                <CargoList
                    excludeCodes={excludeCodes}
                    query={filter?.search}
                    itemClick={handleItemClick}
                    selectedIds={selectedCargos.map(cargo => cargo.id)}
                />
                {selectedCargos.length > 0 && (
                    <FloatingActions>
                        <Button
                            color="additionalPrimary"
                            fullWidth
                            endIcon={(
                                <Badge
                                    badgeContent={selectedCargos.length}
                                    showZero
                                    className={classes.badge}
                                    color="onAdditionalPrimary"
                                />
                            )}
                            onClick={handleSelectClick}
                        >
                            {plural(selectedCargos.length,
                                "Добавить груз", "Добавить грузы", "Добавить грузы")}
                        </Button>
                    </FloatingActions>
                )}
            </AbsoluteContainer>
        </Dialog>
    );
}